/**
 * Drop a new file into the loan tape onboarding (DOP-627).
 *
 * The extractor reads files out of a deal's catalog, so a dropped file is not
 * usable until it is IN a deal and read. This puts it there the way every
 * other add does (the add-files review dialog, then the app-wide upload
 * manager) into the deal the example raw file sits in, then watches the
 * catalog for it and hands it back once the extractor could find it.
 *
 * The watch is a poll of the same search the pickers use, by filename in
 * that deal. It gives up after `WATCH_LIMIT_MS` and says so, rather than
 * spinning forever on a file that failed to upload or to be read.
 *
 * A catalog hit alone is not enough: a month's file often REPLACES one of the
 * same name, and the old record answers the search before the new bytes have
 * landed. So a hit counts only once the upload job carrying this file has
 * finished; a job that failed on it ends the watch with that failure.
 *
 * The name watched is the one the review dialog SENT: a copy kept beside a
 * same-named file is renamed, and the old record must not answer for it. A
 * replacement keeps its name because the backend updates that record in place
 * (`_replace_attachment_in_place`), so once its job finishes the record is the
 * new file.
 *
 * The job's outcome is recorded the moment the manager reports it, not read
 * back from the tray on each tick: a finished job can be dismissed from the
 * tray before the next poll, and its absence then says nothing.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useAddFiles, canAddFilesTo } from "../context/AddFilesContext";
import {
	type UploadJob,
	isJobActive,
	useOptionalUploadManager,
} from "../context/UploadManager";
import type { CatalogDocument } from "./useDocumentMention";
import {
	type FolderTreeNode,
	getUserFolderTree,
} from "../services/fileApi";
import { searchLoanTapeWorkbooks } from "../services/loanTapeDashboardService";

export const WATCH_INTERVAL_MS = 4000;
export const WATCH_LIMIT_MS = 5 * 60 * 1000;
/**
 * How long to wait for the upload job to show up before judging by the
 * catalog alone: a manager that never lists the job (or a finished one
 * cleared before the first look) must not stall the watch for five minutes.
 */
export const JOB_GRACE_MS = 10 * 1000;

export type DropState =
	| { kind: "idle" }
	| { kind: "adding"; filename: string; dealName: string }
	| { kind: "failed"; message: string };

export interface UseLoanTapeFileDrop {
	state: DropState;
	/** Hand dropped files over; only the first `.xlsx` is used. */
	drop: (files: File[]) => void;
	reset: () => void;
}

const findDeal = (
	tree: FolderTreeNode[],
	projectId: string,
): FolderTreeNode | null =>
	tree.find(
		(node) => node.folder.project_id === projectId && canAddFilesTo(node),
	) ?? null;

export function useLoanTapeFileDrop(
	projectId: string | null | undefined,
	onReady: (doc: CatalogDocument) => void,
): UseLoanTapeFileDrop {
	const addFiles = useAddFiles();
	const [state, setState] = useState<DropState>({ kind: "idle" });
	const uploads = useOptionalUploadManager();
	const jobs = uploads?.jobs;
	const hasUploads = uploads !== null;
	const watching = useRef<{
		filename: string;
		folderId: number;
		since: number;
		/** What the upload job said last: "pending" until it finishes. */
		upload: "pending" | "done" | "failed";
		sawJob: boolean;
	} | null>(null);
	const onReadyRef = useRef(onReady);
	onReadyRef.current = onReady;

	const reset = useCallback(() => {
		watching.current = null;
		setState({ kind: "idle" });
	}, []);

	/** Record the watched file's upload outcome from the manager's jobs. */
	const noteJob = useCallback((list: readonly UploadJob[]) => {
		const watch = watching.current;
		if (!watch || watch.upload !== "pending") return;
		// The newest job for this deal that carried this file.
		const job = list
			.filter(
				(entry) =>
					entry.folderId === watch.folderId &&
					entry.files.some((f) => f.name === watch.filename),
			)
			.sort((a, b) => b.startedAt - a.startedAt)[0];
		if (!job) return;
		watch.sawJob = true;
		if (isJobActive(job)) return;
		const failed =
			job.status === "failed" ||
			job.result?.failed.some((path) => path.endsWith(watch.filename)) ||
			job.result?.notAttempted.some((path) => path.endsWith(watch.filename));
		watch.upload = failed ? "failed" : "done";
	}, []);

	const jobsRef = useRef<readonly UploadJob[]>(jobs ?? []);
	jobsRef.current = jobs ?? [];
	// Every change to the jobs, as it happens: a job finishes, THEN it can be
	// dismissed, so its outcome is seen before it goes.
	useEffect(() => {
		if (jobs) noteJob(jobs);
	}, [jobs, noteJob]);

	const drop = useCallback(
		async (files: File[]) => {
			const file = files.find((entry) => /\.xlsx$/i.test(entry.name));
			if (!file) {
				setState({ kind: "failed", message: "Drop an Excel (.xlsx) file." });
				return;
			}
			if (!projectId || !addFiles) {
				setState({
					kind: "failed",
					message:
						"Choose the example first: the new file goes into the same deal.",
				});
				return;
			}
			let deal: FolderTreeNode | null = null;
			try {
				// With its files: the review dialog compares against them to say
				// what is new and what would replace a file already there.
				deal = findDeal(await getUserFolderTree(), projectId);
			} catch {
				deal = null;
			}
			if (!deal) {
				setState({
					kind: "failed",
					message:
						"Couldn't find the example's deal to add the file to. Add it in the Deal Room, then choose it here.",
				});
				return;
			}
			const dealName = deal.folder.name;
			const folderId = deal.folder.id;
			addFiles.openAddFiles({
				deal,
				files: [file],
				onAdded: (sent = []) => {
					// The name that was SENT: the review dialog renames a copy kept
					// beside a same-named file, and the old one must not answer for
					// it. Nothing sent means the file was already there, unchanged.
					const sentFile =
						sent.find((entry) => entry === file) ??
						sent.find((entry) => /\.xlsx$/i.test(entry.name));
					const watchedName = sentFile?.name ?? file.name;
					watching.current = {
						filename: watchedName,
						folderId,
						since: Date.now(),
						upload: "pending",
						sawJob: false,
					};
					noteJob(jobsRef.current);
					setState({ kind: "adding", filename: watchedName, dealName });
				},
			});
		},
		[addFiles, projectId, noteJob],
	);

	useEffect(() => {
		if (state.kind !== "adding") return;
		const timer = window.setInterval(async () => {
			const watch = watching.current;
			if (!watch) return;
			if (Date.now() - watch.since > WATCH_LIMIT_MS) {
				watching.current = null;
				setState({
					kind: "failed",
					message: `${watch.filename} isn't ready yet. Choose it from the Deal Room once it shows there.`,
				});
				return;
			}
			if (hasUploads) {
				noteJob(jobsRef.current);
				if (watch.upload === "failed") {
					watching.current = null;
					setState({
						kind: "failed",
						message: `${watch.filename} didn't upload. Try again, or add it in the Deal Room.`,
					});
					return;
				}
				const unseenTooLong =
					!watch.sawJob && Date.now() - watch.since > JOB_GRACE_MS;
				if (watch.upload === "pending" && !unseenTooLong) return;
			}
			try {
				const docs = await searchLoanTapeWorkbooks(watch.filename, {
					projectId,
				});
				const found = docs.find((doc) => doc.filename === watch.filename);
				if (found && watching.current === watch) {
					watching.current = null;
					setState({ kind: "idle" });
					onReadyRef.current(found);
				}
			} catch {
				// A failed look is retried on the next tick.
			}
		}, WATCH_INTERVAL_MS);
		return () => window.clearInterval(timer);
	}, [state.kind, projectId, hasUploads, noteJob]);

	return { state, drop: (files) => void drop(files), reset };
}
